// CSV upload guide shown in the "CSV format guide" dialog on Step 1 of the
// Upload Wizard (see CsvFormatGuideDialog.tsx).
//
// Source of truth: docs/features/go-lab/datasets-and-annotation/uploading-datasets.md
// This is a curated mirror — CRA/react-scripts can't import files outside
// `src/`, so the guide text is duplicated here. Keep it in sync when that doc
// changes. The doc's H1 and intro line are dropped (the dialog title supplies
// the heading); every other section is reproduced verbatim.

export const CSV_FORMAT_GUIDE_MD = `## File format

- **CSV only**, UTF-8, with a header row.
- **One file = one channel.** All rows must be from the same channel; you confirm which one in the upload wizard.
- One row per customer interaction.

## Columns

| Column | Required | Description | Example |
|---|---|---|---|
| \`verbatim\` | Yes | The full customer interaction text. For transcripts, include the agent turns too. | \`"I've been waiting three weeks for my claim..."\` |
| \`channel\` | Yes | Where the interaction came from. Use one of the channel keys below. Every row should be the same. | \`emails\` |
| \`date\` | Yes | Date of the interaction, \`YYYY-MM-DD\`. Every row must have one — rows without a date are rejected. | \`2026-02-14\` |
| \`journey\` | Recommended | The customer journey stage the interaction relates to. | \`Claims\` |
| \`product\` | Recommended | The product the interaction is about. | \`Motor Insurance\` |

## Channel keys

\`social-media\` · \`reviews\` · \`emails\` · \`call-centre\` · \`live-chat\` · \`complaints\`

## Verbatim format

The \`verbatim\` column is plain text — store the interaction as-is. Two formatting conventions matter for how the annotation UI will display each record:

- **Blank line = new chat bubble.** The annotation flow splits the verbatim on blank lines (\`\\n\\n\` — i.e., one empty line between paragraphs) and renders each paragraph as a separate bubble. Single-paragraph verbatims render as one bubble.
- **\`Customer:\` / \`Agent:\` prefix tags the speaker.** Each paragraph can optionally start with \`Customer:\` or \`Agent:\` (case-insensitive); the prefix is stripped from the bubble's body and used to colour/label the bubble. Paragraphs without a prefix default to the customer side.

The end result: a multi-turn transcript with blank lines between turns renders as an alternating customer / agent chat thread; a single-paragraph social-media post or review renders as one bubble.

### Examples

**Multi-turn call-centre transcript** — three bubbles (customer → agent → customer):

\`\`\`
Customer: My excess is wrong — the website says £100 but you're charging £250.

Agent: Let me check the policy schedule.

Customer: While you're at it, can you cancel my add-on?
\`\`\`

**Single-turn social-media post** — one customer bubble:

\`\`\`
@brand You sent me a renewal quote 60% higher than last year with NO explanation.
\`\`\`

**Tip for CSV authors:** in a quoted CSV cell, a blank line is just \`\\n\\n\` between the two paragraphs you want to split. Most spreadsheet editors and CSV libraries (Excel, Google Sheets, \`csv\` in Python, etc.) preserve embedded newlines inside quoted fields — but double-check after export, since some tools collapse \`\\n\` to a single space.

## Example

\`\`\`csv
verbatim,channel,date,journey,product
"I've been waiting three weeks for my claim and no one has called me back.",emails,2026-02-14,Claims,Motor Insurance
"Customer: Tried to update my address online but the form kept erroring.

Agent: Sorry about that — I can update it for you now. What's the new address?",emails,2026-02-15,Servicing,Home Insurance
\`\`\`

## File limits

- **100 MB maximum file size** per upload. Larger CSVs are rejected at the upload endpoint with a 413 — split the file and upload in parts.
- **100,000 rows maximum** per upload. Same 413 + "split your file" hint applies.

For GO Lab's typical 1k–50k-row uploads, expect end-to-end completion in ~5–20 seconds (mostly dominated by your upload bandwidth, not the database write).

## Row-level validation

The server validates every row as it streams in. Bad rows are skipped, not the whole file:

- **Required**: \`verbatim\` non-empty, \`date\` parseable as \`YYYY-MM-DD\`, \`channel\` matches the platform's channel keys.
- **Optional**: \`journey\`, \`product\`, and any other columns are stored verbatim in source metadata.

After the upload completes, the success message tells you \`{N} records imported\` based on what actually landed. If any rows were rejected, a second message surfaces the count plus the first three reasons (e.g. "row 47: date missing"). The upload-history row for that channel-dataset records the rejected-row count (the per-row reasons are returned in the upload response, not stored in the history). Header errors (e.g. no \`verbatim\` column at all) reject the whole file *before* anything is written — you won't end up with a half-imported state.

## After uploading

Records land in the collection's \`pending\` bucket against the chosen channel. Each record will be annotated against **every taxonomy dimension** the collection covers — the channel you picked determines how the dataset is organised and reported, not which dimensions are annotated.

Annotators pick up new records from the Annotation queue. **Annotation is purely human** — no ML pre-classification or auto-labelling runs against uploaded records. The taxonomy version you chose in Step 3 is captured as audit metadata on the upload-history row; it doesn't trigger any automated processing.
`;
